import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { pbdqFont } from './theme/pbdqTokens';
import { sdsColors } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import PbdqArrowRightIcon from './components/PbdqArrowRightIcon.comp';
import PbdqCosEcosystemWheel from './components/PbdqCosEcosystemWheel.comp';
import PbdqCosAssessmentRow from './components/PbdqCosAssessmentRow.comp';
import PbdqImageLightboxWrapper from './components/PbdqImageLightboxWrapper.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';

import CosAssessmentsHero from '../../assets/images/PoweredByDQ/CosAssessmentsHero.png';
import CosAssessmentsSchoolWorkspace from '../../assets/images/PoweredByDQ/CosAssessmentsSchoolWorkspace.png';
import CosAssessmentsDashboards from '../../assets/images/PoweredByDQ/CosAssessmentsDashboards.png';
import CosAssessmentsApp from '../../assets/images/PoweredByDQ/CosAssessmentsApp.png';
import CosAssessmentsGovtChecklist from '../../assets/images/PoweredByDQ/CosAssessmentsGovtChecklist.png';
import CosAssessmentsAssessReview from '../../assets/images/PoweredByDQ/CosAssessmentsAssessReview.png';
import CosAssessmentsReport from '../../assets/images/PoweredByDQ/CosAssessmentsReport.png';
import CosAssessmentsEveryDeploy1 from '../../assets/images/PoweredByDQ/CosAssessmentsEveryDeploy1.png';
import CosAssessmentsEveryDeploy2 from '../../assets/images/PoweredByDQ/CosAssessmentsEveryDeploy2.png';
import COSAssessmentsEveryDeploy3 from '../../assets/images/PoweredByDQ/COSAssessmentsEveryDeploy3.png';
import CosAssessmentsEveryDeploy4 from '../../assets/images/PoweredByDQ/CosAssessmentsEveryDeploy4.png';

const MOBILE = '@media (max-width: 900px)';
const NARROW = '@media (max-width: 1340px)';

const useStyles = makeStyles(() => ({
    pageWrapper: {
        fontFamily: pbdqFont,
        color: '#1B2430',
        WebkitFontSmoothing: 'antialiased',
        background: '#FFFFFF',
        minHeight: '100vh',
    },

    // --- SECTION 0: Hero (Introduction) ---
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
    },
    heroInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '28px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    heroContent: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        fontFamily: pbdqFont,
        color: '#1B2430',
    },
    heroTitleOrange: {
        display: 'block',
        color: '#ED6628',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
        fontFamily: pbdqFont,
    },
    btnHeroPrimary: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 200ms cubic-bezier(.32, .72, 0, 1), transform 200ms ease',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
            transform: 'scale(1.02)',
        },
    },
    heroArt: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroImg: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    // --- SECTION 1: The ecosystem ---
    ecosystemSection: {
        background: '#FFFFFF',
    },
    ecosystemInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    ecosystemCard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(24px, 3vw, 48px)',
        alignItems: 'center',
        padding: 'clamp(24px, 3vw, 44px)',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        boxSizing: 'border-box',
    },
    ecosystemContent: {
        flex: '1 1 380px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
    },
    sectionChip: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    h2Title: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    ecosystemDesc: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        maxWidth: '56ch',
    },
    ecosystemHint: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
    },
    ecosystemWheelWrap: {
        flex: '1 1 320px',
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
    },

    // --- SECTION 2: What you can assess ---
    assessSection: {
        background: '#F4F6F9',
    },
    assessInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    assessHeaderH2: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: '0 0 32px',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    assessStack: {
        display: 'grid',
        gap: 12,
    },

    // In-depth details inside accordion rows
    inDepthWrap: {
        display: 'grid',
        gap: 22,
        paddingTop: 22,
        borderTop: '1px solid #E6E9EE',
    },
    inDepthBanner: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px clamp(24px, 3vw, 48px)',
        alignItems: 'center',
    },
    inDepthStatBox: {
        display: 'grid',
        gap: 2,
    },
    inDepthStatTag: {
        fontSize: 13,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
    },
    inDepthStatNumber: {
        fontSize: 'clamp(48px, 5vw, 72px)',
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.04em',
        margin: '8px 0 0',
        fontVariantNumeric: 'tabular-nums',
    },
    inDepthStatLabel: {
        fontSize: 13,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#5B6470',
    },
    inDepthTextCol: {
        flex: '1 1 360px',
        minWidth: 0,
        display: 'grid',
        gap: 10,
        fontSize: 15,
        lineHeight: 1.6,
        color: '#5B6470',
    },
    inDepthParagraph: {
        margin: 0,
    },
    inDepthInlineLink: {
        color: '#A63C0C',
        fontWeight: 600,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    subLabel: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#6B7280',
    },
    measuresThreeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
        gap: 16,
        marginTop: -6,
    },
    measureTile: {
        padding: '18px 20px',
        borderRadius: 16,
        display: 'grid',
        gap: 6,
        alignContent: 'start',
    },
    measureTileTitle: {
        fontSize: 16,
        fontWeight: 700,
        margin: 0,
        color: '#1B2430',
    },
    measureTileText: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 0,
        color: '#1B2430',
    },
    specsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: '12px 32px',
        padding: '16px 20px',
        borderRadius: 16,
        border: '1px solid #E6E9EE',
        fontSize: 14.5,
        color: '#1B2430',
    },
    specTitle: {
        display: 'block',
        fontSize: 12,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#6B7280',
        fontWeight: 700,
        marginBottom: 2,
    },
    componentsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 16,
        marginTop: -6,
    },
    componentFigure: {
        margin: 0,
        display: 'grid',
        gap: 12,
        alignContent: 'start',
    },
    componentImg: {
        width: '100%',
        aspectRatio: '16 / 10',
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 14,
        border: '1px solid #E6E9EE',
        display: 'block',
    },
    componentFigcaption: {
        display: 'grid',
        gap: 4,
    },
    componentHeading: {
        fontSize: 16,
        fontWeight: 700,
        margin: '0 0 4px',
        color: '#1B2430',
    },
    componentSubtext: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
    },
    // The 4-dashboard grid inside the Children assessment components figure
    dashGridWrap: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 6,
    },
    dashMiniCell: {
        width: '100%',
        aspectRatio: '16 / 10',
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 10,
        border: '1px solid #E6E9EE',
        display: 'block',
        background: '#F4F6F9',
    },
    dashPillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 8,
    },
    dashPill: {
        padding: '4px 10px',
        borderRadius: 999,
        background: '#EEF3F8',
        fontSize: 12,
        fontWeight: 600,
        color: '#2A4057',
        whiteSpace: 'nowrap',
    },
    inDepthActionRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        paddingTop: 8,
    },
    btnActionPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 14.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    btnActionOutline: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 14.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': {
            background: '#F2F4F7',
            borderColor: '#C4C9D2',
            textDecoration: 'none',
        },
    },

    // Government in-depth styling
    govActionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 12,
        marginTop: -6,
    },
    govActionTile: {
        padding: '16px 18px',
        borderRadius: 16,
        background: '#F8F9FB',
        display: 'grid',
        gap: 4,
    },
    govActionTitle: {
        fontSize: 15,
        fontWeight: 700,
        margin: '0 0 4px',
        color: '#1B2430',
    },
    govActionDesc: {
        fontSize: 14,
        lineHeight: 1.5,
        margin: 0,
        color: '#5B6470',
    },
    contactCard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px 24px',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px',
        borderRadius: 16,
        background: '#F8F9FB',
        border: '1px solid #E6E9EE',
    },
    contactTitle: {
        fontSize: 16,
        fontWeight: 700,
        margin: 0,
        color: '#1B2430',
    },
    contactDesc: {
        fontSize: 14,
        lineHeight: 1.5,
        margin: 0,
        color: '#5B6470',
    },
    btnContactNavy: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 46,
        padding: '0 22px',
        borderRadius: 999,
        background: '#172939',
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease',
        '&:hover': {
            background: '#2A3F54',
            color: '#FFFFFF',
        },
    },

    // --- SECTION 3: What you get ---
    getSection: {
        background: '#FFFFFF',
    },
    getInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    getHeaderRow: {
        display: 'grid',
        gap: 16,
    },
    getHeaderTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    getHeaderDesc: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        maxWidth: '68ch',
        color: '#5B6470',
    },
    deliverablesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 40,
    },
    deliverableCard: {
        margin: 0,
        display: 'grid',
        gap: 14,
        alignContent: 'start',
        padding: '12px 12px 22px',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        background: '#FFFFFF',
    },
    deliverableImg: {
        width: '100%',
        aspectRatio: '4 / 3',
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 12,
        display: 'block',
        background: '#F4F6F9',
    },
    deliverableFigcaption: {
        padding: '0 10px',
        display: 'grid',
        gap: 8,
    },
    deliverableNumberBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        fontSize: 13,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
    },
    deliverableHeading: {
        fontSize: 19,
        lineHeight: 1.2,
        letterSpacing: '-0.015em',
        fontWeight: 700,
        margin: '4px 0 0',
        color: '#1B2430',
    },
    deliverableBody: {
        fontSize: 14.5,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
    },

    // --- SECTION 4: Global scale ---
    globalSection: {
        background: '#FFFFFF',
    },
    globalInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    globalCard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '32px clamp(24px, 4vw, 48px)',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'clamp(28px, 3.5vw, 48px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        boxSizing: 'border-box',
    },
    globalCopyCol: {
        flex: '1.2 1 360px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
        alignContent: 'center',
    },
    globalTitle: {
        fontSize: 'clamp(24px, 2.6vw, 36px)',
        lineHeight: 1.1,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    globalLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
        maxWidth: '70ch',
    },
    globalStoryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 50,
        padding: '0 22px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#172939',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        width: 'fit-content',
        marginTop: 8,
        transition: 'background-color 150ms ease, transform 150ms ease',
        '&:hover': {
            background: '#F2F4F7',
            color: '#172939',
            textDecoration: 'none',
            transform: 'scale(1.02)',
        },
    },
    videoWrap: {
        flex: '1 1 380px',
        minWidth: 0,
        aspectRatio: '1 / 1',
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        background: '#000000',
        maxWidth: 460,
        width: '100%',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        '@media (max-width: 960px)': {
            margin: '0 auto',
            maxWidth: 420,
        },
    },
    videoIframe: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 0,
    },

    // --- SECTION 5: Next step / Final CTA ---
    ctaSection: {
        marginTop: 'clamp(24px, 3vw, 40px)',
        background: '#F4F6F9',
        borderTop: '1px solid #E9ECF0',
    },
    ctaInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    ctaHeading: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '20ch',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    ctaButtons: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    ctaBtnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 200ms ease',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    ctaBtnOutline: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': {
            background: '#F2F4F7',
            borderColor: '#C4C9D2',
            textDecoration: 'none',
        },
    },
}));

const WHAT_IT_MEASURES_CHILDREN = [
    {
        bg: '#E1F0F9',
        border: '#C9E2F2',
        title: 'Online safety readiness',
        desc: 'How well children understand and apply the knowledge, skills, attitudes, and behaviours needed to navigate digital life safely and responsibly.',
    },
    {
        bg: '#F8EAE4',
        border: '#F0D1C3',
        title: 'Online risk experience',
        desc: 'What children are actually encountering — cyberbullying, harmful content, unwanted contact, AI-related risks — not what adults assume they are encountering.',
    },
    {
        bg: '#EDF2D5',
        border: '#DCE6B8',
        title: 'Support around the child',
        desc: 'The access, guidance, conversations, and protective factors children experience at home and at school.',
    },
];

const WHAT_GOVERNMENTS_DO = [
    {
        bg: '#E1F0F9',
        border: '#C9E2F2',
        title: 'Assess national readiness',
        desc: 'Build a standardised view of current system capability.',
    },
    {
        bg: '#EDF2D5',
        border: '#DCE6B8',
        title: 'Validate evidence',
        desc: 'Review the evidence supporting each indicator and proposed score.',
    },
    {
        bg: '#FAEDDA',
        border: '#F3DDB4',
        title: 'Identify gaps',
        desc: 'See where policies, implementation, infrastructure, or services require strengthening.',
    },
    {
        bg: '#F8EAE4',
        border: '#F0D1C3',
        title: 'Prioritise action',
        desc: 'Focus national efforts on the gaps with the greatest potential impact.',
    },
];

const DELIVERABLES = [
    {
        n: '01',
        h: 'The assessment itself',
        img: CosAssessmentsEveryDeploy1,
        alt: 'The assessment itself',
        caption: 'The assessment itself — student and government instrument',
        tint: '#E1F0F9',
        ink: '#1F5F86',
        b: 'A self-administered online assessment — age-appropriate for children, evidence-based for governments — deployed in your languages, on your timeline.',
    },
    {
        n: '02',
        h: 'Live dashboards',
        img: CosAssessmentsEveryDeploy2,
        alt: 'Live dashboards',
        caption: 'Live dashboards — real-time reporting across tiers',
        tint: '#EDF2D5',
        ink: '#4E6A12',
        b: 'National, partner, school and class views that update as responses arrive. No waiting for a report at the end of fieldwork.',
    },
    {
        n: '03',
        h: 'Scorecards for parents and children',
        img: COSAssessmentsEveryDeploy3,
        alt: 'Scorecards for parents and children',
        caption: 'Scorecards for parents and children — strengths and benchmarks',
        tint: '#FAEDDA',
        ink: '#8A5A10',
        b: 'Personal scorecards that help parents and children understand their strengths and weaknesses in online safety — benchmarked against national and global standards.',
    },
    {
        n: '04',
        h: 'Priority-gap report',
        img: CosAssessmentsEveryDeploy4,
        alt: 'Priority-gap report',
        caption: 'Priority-gap report — ranked interventions and roadmap',
        tint: '#F6E5F0',
        ink: '#8E1F55',
        b: 'The gaps ranked by potential impact, with recommended actions — the evidence base for budget cases, policy, and programme design.',
    },
];

const CPC_INDEX_URL = 'https://cpc.gcforum.org/global_cpc';

const PbdqCosAssessmentsPageContent = () => {
    const classes = useStyles();
    const openAgentChat = useAgentChat();

    // 0: Children, 1: Government, 2: Parents, 3: Teachers, 4: School leaders, 5: Ed-techs
    const [openIndex, setOpenIndex] = useState(0);

    const toggleAccordion = (index) => {
        setOpenIndex((prev) => (prev === index ? -1 : index));
    };

    const scrollToAssessList = (index) => {
        setOpenIndex(index);
        const el = document.getElementById('assess-list') || document.getElementById('assess');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className={classes.pageWrapper}>
            <PbdqNav />

            {/* SECTION 0: Introduction (Hero) */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroInner}>
                    <div className={classes.heroContent}>
                        <h1 className={classes.heroTitle}>
                            <span>Establish a trusted national baseline,</span>
                            <span className={classes.heroTitleOrange}>and lead with foresight.</span>
                        </h1>

                        <p className={classes.heroLead}>
                            Child Online Safety (COS) Assessments measure your whole national system — children, government, families, schools, and the platforms reaching them — against global standards. So you can see what is working, what is missing, and where to act first.
                        </p>

                        <button
                            type="button"
                            onClick={() => openAgentChat('COS')}
                            className={classes.btnHeroPrimary}
                        >
                            Build your solution
                            <PbdqArrowRightIcon />
                        </button>
                    </div>

                    <div className={classes.heroArt}>
                        <img
                            src={CosAssessmentsHero}
                            alt="Child Online Safety Assessments ecosystem overview and indicators"
                            className={classes.heroImg}
                        />
                    </div>
                </div>
            </section>

            {/* SECTION 1: The ecosystem */}
            <section aria-label="The ecosystem" className={classes.ecosystemSection}>
                <div className={classes.ecosystemInner}>
                    <div className={classes.ecosystemCard}>
                        <div className={classes.ecosystemContent}>
                            <span className={classes.sectionChip}>The ecosystem</span>
                            <h2 className={classes.h2Title}>A child’s safety is decided by everyone around them</h2>
                            <p className={classes.ecosystemDesc}>
                                No single measurement explains why children are at risk. COS Assessments read the whole system at once — the child at the center, and the five groups whose decisions shape their online life.
                            </p>
                            <p className={classes.ecosystemHint}>
                                Select <strong style={{ color: '#1B2430' }}>the child</strong> or <strong style={{ color: '#1B2430' }}>Government</strong> to open its assessment below — the other four are coming soon.
                            </p>
                        </div>

                        <div className={classes.ecosystemWheelWrap}>
                            <PbdqCosEcosystemWheel
                                activeChild={openIndex === 0}
                                activeGov={openIndex === 1}
                                onSelectChild={() => scrollToAssessList(0)}
                                onSelectGov={() => scrollToAssessList(1)}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2: What you can assess */}
            <section id="assess-list" aria-label="What you can assess" className={classes.assessSection}>
                <div className={classes.assessInner}>
                    <span className={classes.sectionChip}>What you can assess</span>
                    <h2 className={classes.assessHeaderH2}>Six assessments. One picture of the system.</h2>

                    <div className={classes.assessStack} id="assess">
                        {/* 0: COS Children Assessment */}
                        <PbdqCosAssessmentRow
                            title="COS Children Assessment"
                            statusLabel="Core · Available now"
                            isComingSoon={false}
                            question="How ready are our children for life online, and where are they most exposed?"
                            does="Measures children’s online safety readiness, from everyday habits to safety skills · measures their exposure to online risks including cyberbullying, unwanted contact, and harmful content · reads the support around them, at home and at school."
                            delivers="National, partner, school, class and individual dashboards and scorecards · comparisons against national and global benchmarks · clear identification of the priority areas where children need the greatest support."
                            isOpen={openIndex === 0}
                            onToggle={() => toggleAccordion(0)}
                        >
                            <div className={classes.inDepthWrap}>
                                <div className={classes.inDepthBanner}>
                                    <div className={classes.inDepthStatBox}>
                                        <span className={classes.inDepthStatTag}>COS Children Assessment · in depth</span>
                                        <p className={classes.inDepthStatNumber} style={{ color: '#ED6628' }}>15</p>
                                        <span className={classes.inDepthStatLabel}>capability indicators</span>
                                    </div>
                                    <div className={classes.inDepthTextCol}>
                                        <p className={classes.inDepthParagraph}>
                                            The COS Children Assessment measures 15 child capability indicators aligned with the{' '}
                                            <a href={CPC_INDEX_URL} target="_blank" rel="noopener noreferrer" className={classes.inDepthInlineLink}>
                                                Child Protection in Cyberspace (CPC) Index
                                            </a>.
                                        </p>
                                        <p className={classes.inDepthParagraph}>
                                            These indicators complement the 85 system indicators assessed through the COS Government Assessment. Together, they form the evidence base of the{' '}
                                            <a href={CPC_INDEX_URL} target="_blank" rel="noopener noreferrer" className={classes.inDepthInlineLink}>
                                                CPC Index
                                            </a>.
                                        </p>
                                        <p className={classes.inDepthParagraph}>
                                            The COS Children Assessment is available for schools and education systems to measure children’s online safety capabilities and identify priority areas for action.
                                        </p>
                                    </div>
                                </div>

                                <span className={classes.subLabel}>What it measures</span>
                                <div className={classes.measuresThreeGrid}>
                                    {WHAT_IT_MEASURES_CHILDREN.map((item) => (
                                        <div
                                            key={item.title}
                                            className={classes.measureTile}
                                            style={{
                                                background: item.bg,
                                                border: `1px solid ${item.border}`,
                                            }}
                                        >
                                            <h4 className={classes.measureTileTitle}>{item.title}</h4>
                                            <p className={classes.measureTileText}>{item.desc}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className={classes.specsGrid}>
                                    <div>
                                        <span className={classes.specTitle}>Ages</span>
                                        <strong>8–18</strong>
                                    </div>
                                    <div>
                                        <span className={classes.specTitle}>Questions</span>
                                        <strong>179, across 7 sections</strong>
                                    </div>
                                    <div>
                                        <span className={classes.specTitle}>Completion</span>
                                        <strong>~45 minutes</strong>
                                    </div>
                                    <div>
                                        <span className={classes.specTitle}>Deployment</span>
                                        <strong>from a single class to a whole country</strong>
                                    </div>
                                </div>

                                <span className={classes.subLabel}>Solution components</span>
                                <div className={classes.componentsRow}>
                                    <figure className={classes.componentFigure}>
                                        <PbdqImageLightboxWrapper
                                            src={CosAssessmentsSchoolWorkspace}
                                            alt="School workspace screenshot"
                                            caption="School workspace — where schools set up cohorts, run assessment rounds and manage access"
                                        >
                                            <img
                                                src={CosAssessmentsSchoolWorkspace}
                                                alt="School workspace screenshot"
                                                className={classes.componentImg}
                                            />
                                        </PbdqImageLightboxWrapper>
                                        <figcaption className={classes.componentFigcaption}>
                                            <h4 className={classes.componentHeading}>School workspace</h4>
                                            <p className={classes.componentSubtext}>Where schools set up cohorts, run assessment rounds and manage access.</p>
                                        </figcaption>
                                    </figure>

                                    <figure className={classes.componentFigure}>
                                        <PbdqImageLightboxWrapper
                                            src={CosAssessmentsDashboards}
                                            alt="Dashboards screenshot"
                                            caption="Dashboards — results roll up from each child to class, school and national level"
                                        >
                                            <img
                                                src={CosAssessmentsDashboards}
                                                alt="Dashboards screenshot"
                                                className={classes.componentImg}
                                            />
                                        </PbdqImageLightboxWrapper>
                                        <figcaption className={classes.componentFigcaption}>
                                            <h4 className={classes.componentHeading}>Dashboards</h4>
                                            <p className={classes.componentSubtext}>Results roll up from each child to class, school and national level.</p>
                                            <div className={classes.dashPillsRow}>
                                                <span className={classes.dashPill}>National</span>
                                                <span className={classes.dashPill}>Schools</span>
                                                <span className={classes.dashPill}>Classes</span>
                                                <span className={classes.dashPill}>Individuals</span>
                                            </div>
                                        </figcaption>
                                    </figure>

                                    <figure className={classes.componentFigure}>
                                        <PbdqImageLightboxWrapper
                                            src={CosAssessmentsApp}
                                            alt="Assessment app screenshot"
                                            caption="Assessment app — the child-facing assessment experience (ages 8–18)"
                                        >
                                            <img
                                                src={CosAssessmentsApp}
                                                alt="Assessment app screenshot"
                                                className={classes.componentImg}
                                            />
                                        </PbdqImageLightboxWrapper>
                                        <figcaption className={classes.componentFigcaption}>
                                            <h4 className={classes.componentHeading}>Assessment app</h4>
                                            <p className={classes.componentSubtext}>The child-facing assessment experience, ages 8–18.</p>
                                        </figcaption>
                                    </figure>
                                </div>

                                {/* <div className={classes.inDepthActionRow}>
                                    <button
                                        type="button"
                                        onClick={() => openAgentChat('COS')}
                                        className={classes.btnActionPrimary}
                                    >
                                        Build your solution
                                    </button>
                                    <a
                                        href="https://assessment.dqforall.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.btnActionOutline}
                                    >
                                        Preview the assessment
                                    </a>
                                </div> */}
                            </div>
                        </PbdqCosAssessmentRow>

                        {/* 1: COS Government Assessment */}
                        <PbdqCosAssessmentRow
                            title="COS Government Assessment"
                            statusLabel="Core · Available now · Government access only"
                            isComingSoon={false}
                            question="How well does our government protect children online?"
                            does="Measures national readiness to prevent cyber risks to children, to protect children from harm, and to empower them to thrive online, across 85 system indicators."
                            delivers="A national dashboard of government scorecards against global benchmarks, with priority gaps flagged · the 85 system indicators that, with 15 children’s indicators, form the basis of the CPC Index."
                            isOpen={openIndex === 1}
                            onToggle={() => toggleAccordion(1)}
                        >
                            <div className={classes.inDepthWrap}>
                                <div className={classes.inDepthBanner}>
                                    <div className={classes.inDepthStatBox}>
                                        <span className={classes.inDepthStatTag}>COS Government Assessment · in depth</span>
                                        <p className={classes.inDepthStatNumber} style={{ color: '#172939' }}>85</p>
                                        <span className={classes.inDepthStatLabel}>system indicators</span>
                                    </div>
                                    <div className={classes.inDepthTextCol}>
                                        <p className={classes.inDepthParagraph}>
                                            The COS Government Assessment measures 85 system indicators covering the policy, institutional, industry, education and protection environment surrounding children online.
                                        </p>
                                        <p className={classes.inDepthParagraph}>
                                            These indicators complement the 15 child capability indicators from the COS Children Assessment. Together, they form the basis of the{' '}
                                            <a href={CPC_INDEX_URL} target="_blank" rel="noopener noreferrer" className={classes.inDepthInlineLink}>
                                                CPC Index
                                            </a>.
                                        </p>
                                        <p className={classes.inDepthParagraph}>
                                            The Government Assessment is available <strong style={{ color: '#1B2430' }}>only to authorized government officials</strong> participating in the{' '}
                                            <a href={CPC_INDEX_URL} target="_blank" rel="noopener noreferrer" className={classes.inDepthInlineLink}>
                                                CPC Index
                                            </a> process. It is currently accessed through the <strong style={{ color: '#1B2430' }}>Global Cybersecurity Forum</strong>.
                                        </p>
                                    </div>
                                </div>

                                <span className={classes.subLabel}>What it helps governments do</span>
                                <div className={classes.govActionsGrid}>
                                    {WHAT_GOVERNMENTS_DO.map((item) => (
                                        <div
                                            key={item.title}
                                            className={classes.govActionTile}
                                            style={{
                                                background: item.bg,
                                                border: `1px solid ${item.border}`,
                                            }}
                                        >
                                            <h4 className={classes.govActionTitle}>{item.title}</h4>
                                            <p className={classes.govActionDesc}>{item.desc}</p>
                                        </div>
                                    ))}
                                </div>

                                <span className={classes.subLabel}>Solution components</span>
                                <div className={classes.componentsRow}>
                                    <figure className={classes.componentFigure}>
                                        <PbdqImageLightboxWrapper
                                            src={CosAssessmentsGovtChecklist}
                                            alt="COS Government checklist"
                                            caption="COS Government checklist — the 85 system indicators as a structured self-assessment checklist"
                                        >
                                            <img
                                                src={CosAssessmentsGovtChecklist}
                                                alt="COS Government checklist"
                                                className={classes.componentImg}
                                            />
                                        </PbdqImageLightboxWrapper>
                                        <figcaption className={classes.componentFigcaption}>
                                            <h4 className={classes.componentHeading}>COS Government checklist</h4>
                                            <p className={classes.componentSubtext}>The 85 system indicators as a structured self-assessment checklist.</p>
                                        </figcaption>
                                    </figure>

                                    <figure className={classes.componentFigure}>
                                        <PbdqImageLightboxWrapper
                                            src={CosAssessmentsAssessReview}
                                            alt="Assessment review"
                                            caption="Assessment review — where evidence behind each indicator and proposed score is reviewed"
                                        >
                                            <img
                                                src={CosAssessmentsAssessReview}
                                                alt="Assessment review"
                                                className={classes.componentImg}
                                            />
                                        </PbdqImageLightboxWrapper>
                                        <figcaption className={classes.componentFigcaption}>
                                            <h4 className={classes.componentHeading}>Assessment review</h4>
                                            <p className={classes.componentSubtext}>Where evidence behind each indicator and proposed score is reviewed.</p>
                                        </figcaption>
                                    </figure>

                                    <figure className={classes.componentFigure}>
                                        <PbdqImageLightboxWrapper
                                            src={CosAssessmentsReport}
                                            alt="National readiness report"
                                            caption="National readiness report — standardised scorecards and priority gaps for national action"
                                        >
                                            <img
                                                src={CosAssessmentsReport}
                                                alt="National readiness report"
                                                className={classes.componentImg}
                                            />
                                        </PbdqImageLightboxWrapper>
                                        <figcaption className={classes.componentFigcaption}>
                                            <h4 className={classes.componentHeading}>Report</h4>
                                            <p className={classes.componentSubtext}>Standardised scorecards and priority gaps for national action.</p>
                                        </figcaption>
                                    </figure>
                                </div>

                                <div className={classes.contactCard}>
                                    <div>
                                        <h4 className={classes.contactTitle}>Interested in joining the CPC Index?</h4>
                                        <p className={classes.contactDesc}>Contact us to explore participation for your government or country.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => openAgentChat('COS Government')}
                                        className={classes.btnContactNavy}
                                    >
                                        Contact us
                                    </button>
                                </div>
                            </div>
                        </PbdqCosAssessmentRow>

                        {/* 2: COS Parent Assessment */}
                        <PbdqCosAssessmentRow
                            title="COS Parent Assessment"
                            statusLabel="Coming soon"
                            isComingSoon={true}
                            question="How ready are our parents to raise their children safely online?"
                            does="Measures capability to guide and protect children online · measures access to cyber-parenting tools, resources, and networks."
                            delivers="A national dashboard and globally benchmarked parent-readiness scorecards · clear identification of gaps in family support and where cyber-parenting resources are needed most · register your interest to be notified when the assessment becomes available."
                            isOpen={openIndex === 2}
                            onToggle={() => toggleAccordion(2)}
                            onWaitlistClick={() => openAgentChat('COS Parent Assessment')}
                        />

                        {/* 3: COS Teacher Assessment */}
                        <PbdqCosAssessmentRow
                            title="COS Teacher Assessment"
                            statusLabel="Coming soon"
                            isComingSoon={true}
                            question="How well can our teachers educate and protect students online?"
                            does="Measures capability to deliver digital citizenship education · measures practice in keeping students safe online."
                            delivers="A role-specific dashboard of teacher capability by school and region · the training priorities for professional development · register interest and we’ll tell you when it becomes available."
                            isOpen={openIndex === 3}
                            onToggle={() => toggleAccordion(3)}
                            onWaitlistClick={() => openAgentChat('COS Teacher Assessment')}
                        />

                        {/* 4: COS School Leader Assessment */}
                        <PbdqCosAssessmentRow
                            title="COS School Leader Assessment"
                            statusLabel="Coming soon"
                            isComingSoon={true}
                            question="How well do our schools keep students safe online?"
                            does="Measures the quality of digital citizenship education delivered · digital safety practice in day-to-day operation · the policies and governance holding it in place."
                            delivers="A school-level readiness dashboard against national and global benchmarks · the policy and governance gaps to close first · register interest and we’ll tell you when it becomes available."
                            isOpen={openIndex === 4}
                            onToggle={() => toggleAccordion(4)}
                            onWaitlistClick={() => openAgentChat('COS School Leader Assessment')}
                        />

                        {/* 5: COS Ed-Tech Assessment */}
                        <PbdqCosAssessmentRow
                            title="COS Ed-Tech Assessment"
                            statusLabel="Coming soon"
                            isComingSoon={true}
                            question="How safely do our ed-tech companies build for children?"
                            does="Measures the safety built into the products children use · measures the company processes that keep it in place."
                            delivers="A product-safety scorecard against global child-safety standards · the design and process gaps to remediate · register interest and we’ll tell you when it becomes available."
                            isOpen={openIndex === 5}
                            onToggle={() => toggleAccordion(5)}
                            onWaitlistClick={() => openAgentChat('COS Ed-Tech Assessment')}
                        />
                    </div>
                </div>
            </section>

            {/* SECTION 3: What you get */}
            <section aria-label="What you get" className={classes.getSection}>
                <div className={classes.getInner}>
                    <span className={classes.sectionChip}>What you get</span>
                    <div className={classes.getHeaderRow}>
                        <h2 className={classes.getHeaderTitle}>Every deployment delivers four things</h2>
                        <p className={classes.getHeaderDesc}>
                            From the assessment itself to the decisions it enables — this is what lands in your hands, whether you deploy to one class or a whole country.
                        </p>
                    </div>

                    <div className={classes.deliverablesGrid}>
                        {DELIVERABLES.map((d) => (
                            <figure key={d.n} className={classes.deliverableCard}>
                                <PbdqImageLightboxWrapper
                                    src={d.img}
                                    alt={d.alt}
                                    caption={d.caption}
                                >
                                    <img
                                        loading="lazy"
                                        src={d.img}
                                        alt={d.alt}
                                        className={classes.deliverableImg}
                                    />
                                </PbdqImageLightboxWrapper>
                                <figcaption className={classes.deliverableFigcaption}>
                                    <span
                                        className={classes.deliverableNumberBadge}
                                        style={{ background: d.tint, color: d.ink }}
                                    >
                                        {d.n}
                                    </span>
                                    <h4 className={classes.deliverableHeading}>{d.h}</h4>
                                    <p className={classes.deliverableBody}>{d.b}</p>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 4: Global scale */}
            <section aria-label="Global scale" className={classes.globalSection}>
                <div className={classes.globalInner}>
                    <div className={classes.globalCard}>
                        <div className={classes.globalCopyCol}>
                            <h3 className={classes.globalTitle}>
                                This is what it looks like at global scale
                            </h3>
                            <p className={classes.globalLead}>
                                The Global Cybersecurity Forum built the{' '}
                                <a
                                    href={CPC_INDEX_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#FFFFFF', fontWeight: 600, textDecoration: 'underline' }}
                                >
                                    CPC Index
                                </a>{' '}
                                on this instrument, with ITU, UNICEF, WeProtect, and DQ Institute — reaching 100+ countries with a single mission: cut child cyber-risk by 15% in five years.
                            </p>

                            <Link to={pbdqRoutes.impact} className={classes.globalStoryBtn}>
                                Read the CPC Index story →
                            </Link>
                        </div>

                        {/* Video code matching Solutions page reference */}
                        <div className={classes.videoWrap}>
                            <iframe
                                src="https://player.vimeo.com/video/1219565994?badge=0&autopause=0&autoplay=1&muted=1&loop=1&player_id=0&app_id=58479"
                                title="Introducing the CPC Index Powered by DQ"
                                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                                allowFullScreen
                                className={classes.videoIframe}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 5: Next step / Final CTA */}
            <section aria-label="Next step" className={classes.ctaSection}>
                <div className={classes.ctaInner}>
                    <h3 className={classes.ctaHeading}>From a single class to a whole country.</h3>
                    <div className={classes.ctaButtons}>
                        <Link to={pbdqRoutes.getStarted} className={classes.ctaBtnPrimary}>
                            Get started
                        </Link>
                        <Link to={pbdqRoutes.pricing} className={classes.ctaBtnOutline}>
                            Be a DQ Pro
                        </Link>
                    </div>
                </div>
            </section>

            <PbdqFooter />
        </div>
    );
};

export default PbdqCosAssessmentsPageContent;
